import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Btn, DISCIPLINES, Loading, PageTitle, Pick } from "@/components/app/bits";

const ROLES = ["Admin", "Manager", "Field", "Viewer"];
const Card = ({ title, text, children, testid }) => (
  <section className="panel p-5 space-y-4" data-testid={testid}>
    <div><h2 className="text-xl font-semibold text-slate-100">{title}</h2>{text && <p className="text-sm text-slate-400">{text}</p>}</div>
    {children}
  </section>
);

function Stages({ isAdmin }) {
  const { me, refresh } = useAuth();
  const org = me.orgs.find((o) => o.id === me.active_org_id);
  const [names, setNames] = useState(org.stage_names);
  useEffect(() => { setNames(org.stage_names); }, [org]);
  const save = async () => { try { await api.put("/org/stages", { stage_names: names }); await refresh(); toast.success("Stage names saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Stage names" text="Checksheet templates are grouped into stages A, B and C. Rename them to suit your project." testid="settings-stages">
      <div className="grid md:grid-cols-3 gap-3">
        {["A", "B", "C"].map((s) => <label key={s} className="text-xs font-mono text-slate-500">STAGE {s}<Input disabled={!isAdmin} value={names[s]} onChange={(e) => setNames({ ...names, [s]: e.target.value })} data-testid={`stage-name-${s}`} /></label>)}
      </div>
      {isAdmin && <Btn onClick={save} data-testid="save-stages-btn">Save stage names</Btn>}
    </Card>
  );
}

function Packs({ isAdmin, onChange }) {
  const [packs, setPacks] = useState([]);
  const load = useCallback(() => api.get("/packs").then((r) => setPacks(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async (p) => { try { const r = await api.post(`/packs/${p.id}/load`); toast.success(`${r.data.templates_added} templates and ${r.data.equipment_types_added} equipment types added`); load(); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Template packs" text="Load an industry pack to add its equipment types and A, B and C checksheets. Everything can be edited afterwards." testid="settings-packs">
      <div className="grid md:grid-cols-2 gap-3">
        {packs.map((p) => (
          <div key={p.id} className="border border-[#1E2D4A] p-4 flex justify-between gap-3" data-testid={`pack-${p.id}`}>
            <div><p className="font-semibold text-slate-100">{p.name}</p><p className="text-xs text-slate-400">{p.description}</p><p className="text-xs text-slate-500 font-mono mt-1">{p.templates} TEMPLATES · {p.equipment_types} EQUIPMENT TYPES</p></div>
            {p.loaded ? <span className="text-xs text-emerald-400 font-mono shrink-0">LOADED</span> : isAdmin && <Btn variant="ghost" className="shrink-0 !py-1" onClick={() => add(p)} data-testid={`load-pack-${p.id}`}>Load</Btn>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function EquipmentDialog({ eq, templates, close, onSaved }) {
  const [f, setF] = useState(eq);
  useEffect(() => { setF(eq); }, [eq]);
  if (!f) return null;
  const toggle = (id) => setF({ ...f, template_ids: f.template_ids.includes(id) ? f.template_ids.filter((x) => x !== id) : [...f.template_ids, id] });
  const save = async () => {
    const body = { name: f.name, discipline: f.discipline, template_ids: f.template_ids };
    try { f.id ? await api.put(`/equipment-types/${f.id}`, body) : await api.post("/equipment-types", body); toast.success("Equipment type saved"); close(); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#0F172A] border-[#1E2D4A] max-h-[90vh] overflow-y-auto" data-testid="equipment-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{f.id ? "Edit equipment type" : "New equipment type"}</DialogTitle></DialogHeader>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid="equipment-name" />
        <Pick testid="equipment-discipline" value={f.discipline} onChange={(v) => setF({ ...f, discipline: v })} options={DISCIPLINES} />
        <p className="text-xs font-mono text-slate-500 uppercase">Checksheets generated for each tag</p>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {templates.map((t) => <label key={t.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.template_ids.includes(t.id)} onCheckedChange={() => toggle(t.id)} /><span className="num text-amber-400 text-xs">{t.code}</span>{t.name}</label>)}
        </div>
        <Btn onClick={save} disabled={!f.name || !f.discipline} data-testid="equipment-save">Save</Btn>
      </DialogContent>
    </Dialog>
  );
}

function TemplateDialog({ tpl, close, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { setF(tpl ? { ...tpl, itemsText: (tpl.items || []).join("\n") } : null); }, [tpl]);
  if (!f) return null;
  const save = async () => {
    const body = { code: f.code, name: f.name, stage: f.stage, discipline: f.discipline, items: f.itemsText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try { f.id ? await api.put(`/templates/${f.id}`, body) : await api.post("/templates", body); toast.success("Template saved. Applies to checksheets generated from now on."); close(); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#0F172A] border-[#1E2D4A] max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{f.id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="Code, e.g. I-B02" data-testid="template-code" />
          <Pick testid="template-stage" value={f.stage} onChange={(v) => setF({ ...f, stage: v })} options={["A", "B", "C"]} />
        </div>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid="template-name" />
        <Pick testid="template-discipline" value={f.discipline} onChange={(v) => setF({ ...f, discipline: v })} options={DISCIPLINES} />
        <Textarea rows={7} value={f.itemsText} onChange={(e) => setF({ ...f, itemsText: e.target.value })} placeholder="One check item per line" data-testid="template-items" />
        <Btn onClick={save} disabled={!f.code || !f.name || !f.itemsText.trim()} data-testid="template-save">Save template</Btn>
      </DialogContent>
    </Dialog>
  );
}

function Library({ isAdmin, version }) {
  const [eqs, setEqs] = useState(null);
  const [tpls, setTpls] = useState([]);
  const [editEq, setEditEq] = useState(null);
  const [editTpl, setEditTpl] = useState(null);
  const load = useCallback(() => Promise.all([api.get("/equipment-types"), api.get("/templates")]).then(([a, b]) => { setEqs(a.data); setTpls(b.data); }), []);
  useEffect(() => { load(); }, [load, version]);
  if (!eqs) return <Loading />;
  const code = (id) => tpls.find((t) => t.id === id)?.code;
  return (
    <>
      <Card title="Equipment types" text="Each tag's equipment type decides which checksheets are generated." testid="settings-equipment">
        {isAdmin && <Btn variant="ghost" onClick={() => setEditEq({ name: "", discipline: "mechanical", template_ids: [] })} data-testid="new-equipment-btn"><Plus className="w-4 h-4" />New equipment type</Btn>}
        <div className="divide-y divide-[#1E2D4A] max-h-96 overflow-y-auto border border-[#1E2D4A]">
          {eqs.map((e) => (
            <div key={e.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`equipment-row-${e.id}`}>
              <div><span className="text-slate-100">{e.name}</span> <span className="text-xs text-slate-500 capitalize">· {e.discipline}</span>
                <p className="num text-[11px] text-slate-500">{e.template_ids.map(code).join("  ") || "No templates"}</p></div>
              {isAdmin && <button onClick={() => setEditEq(e)} className="p-1.5 text-slate-400 hover:text-amber-400" aria-label="Edit"><Pencil className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      </Card>
      <Card title="Checksheet templates" testid="settings-templates">
        {isAdmin && <Btn variant="ghost" onClick={() => setEditTpl({ code: "", name: "", stage: "A", discipline: "mechanical", items: [] })} data-testid="new-template-btn"><Plus className="w-4 h-4" />New template</Btn>}
        <div className="divide-y divide-[#1E2D4A] max-h-96 overflow-y-auto border border-[#1E2D4A]">
          {tpls.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`template-row-${t.code}`}>
              <div><span className="num text-amber-400 text-xs mr-2">{t.code}</span><span className="text-slate-100">{t.name}</span><span className="text-xs text-slate-500"> · Stage {t.stage} · {t.items.length} items</span></div>
              {isAdmin && <button onClick={() => setEditTpl(t)} className="p-1.5 text-slate-400 hover:text-amber-400" aria-label="Edit"><Pencil className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      </Card>
      <EquipmentDialog eq={editEq} templates={tpls} close={() => setEditEq(null)} onSaved={load} />
      <TemplateDialog tpl={editTpl} close={() => setEditTpl(null)} onSaved={load} />
    </>
  );
}

function Members({ isAdmin }) {
  const { me } = useAuth();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ email: "", role: "Field" });
  const load = useCallback(() => api.get("/org/members").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load, me.active_org_id]);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!rows) return <Loading />;
  return (
    <Card title="Users and roles" text="Unlimited users. Invite by Google account email. Viewers (client, lender, auditor) have read-only access." testid="settings-members">
      {isAdmin && (
        <div className="flex flex-col md:flex-row gap-2">
          <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@company.com" data-testid="invite-email" className="md:w-80" />
          <Pick testid="invite-role" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={ROLES} className="md:w-40" />
          <Btn onClick={() => act(() => api.post("/org/members", f), "User invited").then(() => setF({ email: "", role: "Field" }))} disabled={!f.email.includes("@")} data-testid="invite-btn">Invite</Btn>
        </div>
      )}
      <div className="divide-y divide-[#1E2D4A] border border-[#1E2D4A]">
        {rows.map((u) => (
          <div key={u.user_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`member-row-${u.email}`}>
            <div className="min-w-0"><p className="text-slate-100 truncate">{u.name || "Invited, not signed in yet"}</p><p className="text-xs text-slate-500 truncate">{u.email}</p></div>
            <div className="flex items-center gap-2">
              {isAdmin && u.user_id !== me.user_id ? <Pick testid={`member-role-${u.email}`} value={u.role} onChange={(v) => v && act(() => api.put(`/org/members/${u.user_id}`, { role: v }), "Role updated")} options={ROLES} className="w-32" />
                : <span className="font-mono text-xs text-amber-400">{u.role}</span>}
              {isAdmin && u.user_id !== me.user_id && <button onClick={() => window.confirm(`Remove ${u.email}?`) && act(() => api.delete(`/org/members/${u.user_id}`), "User removed")} className="p-1.5 text-slate-500 hover:text-red-400" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Digest({ isAdmin }) {
  const { me, can } = useAuth();
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/org").then((r) => setOn(r.data.weekly_digest !== false)).catch(() => {}); }, [me.active_org_id]);
  const toggle = async (v) => { try { await api.put("/org/digest", { enabled: v }); setOn(v); toast.success(v ? "Weekly email turned on" : "Weekly email turned off"); } catch (e) { toast.error(errMsg(e)); } };
  const test = async () => { try { const r = await api.post("/org/digest/test"); toast.success(`Test email sent to ${r.data.sent_to}`); } catch (e) { toast.error(errMsg(e)); } };
  if (on === null) return null;
  return (
    <Card title="Weekly forecast email" text="Every Monday at 07:00 UK time, Admins and Managers receive each project's milestone targets, forecasts, variance and what is driving the date." testid="settings-digest">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-200"><Switch checked={on} onCheckedChange={toggle} disabled={!isAdmin} data-testid="digest-toggle" />{on ? "On" : "Off"}</label>
        {can("Manager") && <Btn variant="ghost" onClick={test} data-testid="digest-test-btn">Send me a test now</Btn>}
      </div>
    </Card>
  );
}

function NewOrg() {
  const { refresh } = useAuth();
  const [name, setName] = useState("");
  const create = async () => { try { await api.post("/orgs", { name }); await refresh(); setName(""); toast.success("Organisation created and selected"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Organisations" text="As platform owner you can create a new customer organisation, then invite its Admin." testid="settings-new-org">
      <div className="flex flex-col md:flex-row gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Organisation name" className="md:w-80" data-testid="settings-new-org-name" /><Btn onClick={create} disabled={!name.trim()} data-testid="settings-create-org-btn">Create organisation</Btn></div>
    </Card>
  );
}

export default function SettingsPage() {
  const { me, can } = useAuth();
  const [version, setVersion] = useState(0);
  const isAdmin = can("Admin");
  return (
    <div className="space-y-5" data-testid="settings-page" key={me.active_org_id}>
      <PageTitle eyebrow="Settings" title="Organisation configuration" />
      {!isAdmin && <p className="text-sm text-slate-400">You have {me.role} access. Only Admins can change configuration.</p>}
      <Stages isAdmin={isAdmin} />
      <Digest isAdmin={isAdmin} />
      <Packs isAdmin={isAdmin} onChange={() => setVersion((v) => v + 1)} />
      <Library isAdmin={isAdmin} version={version} />
      {can("Manager") && <Members isAdmin={isAdmin} />}
      {me.is_owner && <NewOrg />}
    </div>
  );
}
